import { useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import uniqBy from 'lodash.uniqby';

import { MeWithSelectedIdentities } from '../../../../graphql/queries/account';
import { AlbumFolderWithPurchasableItems } from '../../../../graphql/queries/album-folder';
import { ALL_PHOTOS_MODE } from '../../../../constants/album-folder';

const usePrepaidItemsPurchase = ({
  albumFolderId,
  isOnboarding,
  jerseyColors = [],
}) => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: userWithIdentities } = {}, loading: loadingIdentities } =
    useQuery(MeWithSelectedIdentities, {
      variables: {
        albumFolderIds: [albumFolderId],
      },
      fetchPolicy: 'network-only',
      skip: !albumFolderId || !authenticated,
    });
  const { data: { albumFolder } = {} } = useQuery(
    AlbumFolderWithPurchasableItems,
    {
      variables: {
        id: albumFolderId,
      },
      skip: !albumFolderId,
    },
  );
  const onboardingSelectedIdentities = useSelector(
    ({ discoverAlbumFolder }) => discoverAlbumFolder.identities,
  );

  const identities = useMemo(() => {
    if (isOnboarding) {
      return onboardingSelectedIdentities;
    }

    return uniqBy(
      userWithIdentities?.selected_identities?.records ?? [],
      ({ identity }) => identity.id,
    );
  }, [userWithIdentities, onboardingSelectedIdentities, isOnboarding]);

  const albumCustomPrints = useMemo(() => {
    if (jerseyColors?.length > 0) {
      return (albumFolder?.album_custom_prints?.records ?? [])?.filter(
        ({ custom_print: customPrint }) =>
          !customPrint?.jersey_colors?.records?.length ||
          !!customPrint?.jersey_colors?.records?.find(
            ({ jersey_color: color }) => jerseyColors.includes(color),
          ),
      );
    }

    return albumFolder?.album_custom_prints?.records ?? [];
  }, [albumFolder, jerseyColors]);

  const printPackages = useMemo(() => {
    if (jerseyColors?.length > 0) {
      return (albumFolder?.album_folder_print_packages?.records ?? [])?.filter(
        ({ print_package: printPackage }) =>
          !printPackage?.jersey_colors?.records?.length ||
          !!printPackage?.jersey_colors?.records?.find(
            ({ jersey_color: color }) => jerseyColors.includes(color),
          ),
      );
    }

    return albumFolder?.album_folder_print_packages?.records ?? [];
  }, [albumFolder, jerseyColors]);

  return {
    albumFolder,
    loadingIdentities,
    identities,
    selectedIdentities: userWithIdentities?.selected_identities?.records ?? [],
    printPackages,
    photoPricing: albumFolder?.photo_pricing,
    albumCustomPrints,
    digitalBundles: albumFolder?.digital_bundles?.records ?? [],
    activePromoCode: albumFolder?.active_promo_codes?.records?.[0],
    hasNoFaceRec:
      albumFolder?.matching_disabled &&
      albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE,
  };
};

export default usePrepaidItemsPurchase;
